/**
 * New Password Required Form Component
 * 初回ログイン時のパスワード変更フォーム
 * Requirements: 6.2, 6.5
 */
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './DesignLoginForm.scss';

const newPasswordSchema = z.object({
  newPassword: z
    .string()
    .min(8, 'パスワードは8文字以上で入力してください')
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/,
      'パスワードは大文字、小文字、数字、特殊文字を含む必要があります'
    ),
  confirmPassword: z.string().min(1, '確認用パスワードを入力してください'),
}).refine((data) => data.newPassword === data.confirmPassword, {
  message: 'パスワードが一致しません',
  path: ['confirmPassword'],
});

type NewPasswordFormData = z.infer<typeof newPasswordSchema>;

export const NewPasswordRequiredForm: React.FC = () => {
  const { completeNewPassword, isLoading, error, clearError, pendingUsername } = useAuth();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<NewPasswordFormData>({
    resolver: zodResolver(newPasswordSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  });

  const newPasswordValue = watch('newPassword');
  const confirmPasswordValue = watch('confirmPassword');

  const onSubmit = async (data: NewPasswordFormData) => {
    try {
      clearError();
      await completeNewPassword(data.newPassword);
      navigate('/dashboard');
    } catch {
      // Error is handled by AuthContext
    }
  };

  return (
    <div className="design-login-page">
      <div className="l-all-wrap">
        <header id="l-header" className="l-header">
          <div className="inner">
            <p className="logo">
              <Link to="/" className="logo_link">
                <img src="/mock/terrace.png" alt="GB照らす" />
              </Link>
            </p>
          </div>
        </header>

        <main className="l-main">
          <section className="p-login">
            <div className="login">
              <form
                className="login_form"
                onSubmit={handleSubmit(onSubmit)}
                noValidate
              >
                <h1 className="login_ttl">初回パスワード変更</h1>
                
                <p className="login_description">
                  {pendingUsername && <strong>{pendingUsername}</strong>}さん、初回ログインのため新しいパスワードを設定してください。
                </p>

                {error && (
                  <div className="login_error_alert">
                    {error}
                  </div>
                )}

                <ul className="login_list">
                  <li className="login_list_item">
                    <div className="login_list_box">
                      <label className={`login_list_label ${!newPasswordValue ? 'placeholder-shown' : ''}`}>
                        <input
                          {...register('newPassword')}
                          className="login_list_label_input"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="新しいパスワード"
                          autoComplete="new-password"
                          autoFocus
                          disabled={isLoading}
                        />
                      </label>
                      <button
                        className={`login_list_label_toggle ${showPassword ? 'show-password' : 'hide-password'}`}
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? 'パスワードを隠す' : 'パスワードを表示'}
                      />
                    </div>
                    <p className="login_list_note">8文字以上、大文字・小文字・数字・特殊文字を含む</p>
                    {errors.newPassword && (
                      <p className="login_list_error">{errors.newPassword.message}</p>
                    )}
                  </li>
                  <li className="login_list_item">
                    <div className="login_list_box">
                      <label className={`login_list_label ${!confirmPasswordValue ? 'placeholder-shown' : ''}`}>
                        <input
                          {...register('confirmPassword')}
                          className="login_list_label_input"
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="新しいパスワード（確認）"
                          autoComplete="new-password"
                          disabled={isLoading}
                        />
                      </label>
                      <button
                        className={`login_list_label_toggle ${showConfirmPassword ? 'show-password' : 'hide-password'}`}
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        aria-label={showConfirmPassword ? 'パスワードを隠す' : 'パスワードを表示'}
                      />
                    </div>
                    {errors.confirmPassword && (
                      <p className="login_list_error">{errors.confirmPassword.message}</p>
                    )}
                  </li>
                </ul>

                <button
                  className="login_btn"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? <span className="login_loading" /> : 'パスワードを変更'}
                </button>
              </form>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};
